import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useLoader } from "@/hooks/LoaderProvider";
import { ArrowRightOutlined } from "@/config/antd";
import { selectUserInfo } from "@/store/redux/slices/auth/auth_slice";
import {
  getAllManifestos,
  selectManifestos,
} from "@/store/redux/slices/manifesto/manifesto_slice";
import {
  createEvent,
  fetchEventByAny,
  updateEvent,
} from "@/store/redux/slices/event/event_slice";
import { getOrganizationId, getAccessToken } from "@/utils/token_functions";
import Button from "@/components/common/Button";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import CustomScheduleInput from "@/components/specific/Custom_Schedule_Input";
import Success from "@/components/common/alerts/Success";
import { getFirstName } from "@/utils/nameUtils";
import Breadcrumb from "@/components/specific/Breadcrumb";
import MemberPicker from "@/components/specific/Member_Picker";
import InviteMembersDialog from "@/ui/Invite_Members";
import Delete from "@/components/common/alerts/Delete";
import FileInput from "@/components/common/inputs/File_Input";
import { uploadFile } from "@/store/redux/slices/user/file_upload_slice";
import LocationInput from "@/components/common/inputs/Location_Input";
import StepsComponent from "@/components/common/Steps";

const AddEvent = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const userFirstName = getFirstName(userInfo?.fullname);
  const manifestos = useSelector(selectManifestos);
  const { id } = useParams(); // Get ID if in edit mode
  const isEditMode = Boolean(id);

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [formData, setFormData] = useState({
    ...(isEditMode && { _id: "" }), // Only include _id if in edit mode
    name: "",
    type: "",
    description: "",
    fileUrl: "",
    duration: "",
    customDate: "",
    customTimeFrom: "",
    customTimeTo: "",
    position: "",
    manifestoID: "",
    location: "",
    candidates: [],
    members: [],
    isInviteMembersModalVisible: false,
    isDeleteModalVisible: false,
    memberToDelete: null,
    isSuccessModalVisible: false,
  });

  // Define the sections for tracking in the Steps component
  const formSections = [
    { title: "Event Information" },
    { title: "Event Image" },
    { title: "Members & Manifesto" },
    { title: "Schedule" },
    { title: "Location" },
  ];

  // State to track the current active section
  const [currentSection, setCurrentSection] = useState(0);

  // Track which sections have been completed
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  // Refs for each section to detect visibility
  const sectionRefs = useRef([]);

  // Check if a section is complete
  const checkSectionCompletion = useCallback(() => {
    const completed = [];

    // Section 0: Event Information
    if (formData.name && formData.type && formData.description) {
      completed.push(0);
    }

    // Section 1: Event Image
    if (formData.fileUrl) {
      completed.push(1);
    }

    // Section 2: Members & Manifesto
    // Only check if this section is applicable based on event type
    if (
      !["Campaign", "Sensitization"].includes(formData.type) ||
      (formData.members.length > 0 && formData.manifestoID)
    ) {
      completed.push(2);
    }

    // Section 3: Schedule
    if (
      formData.duration &&
      (formData.duration !== "custom" ||
        (formData.customDate &&
          formData.customTimeFrom &&
          formData.customTimeTo))
    ) {
      completed.push(3);
    }

    // Section 4: Location
    if (formData.location) {
      completed.push(4);
    }

    setSectionsCompleted(completed);
  }, [formData]);

  // Check section completion whenever formData changes
  useEffect(() => {
    checkSectionCompletion();
  }, [formData, checkSectionCompletion]);

  // Function to check which section is currently most visible
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      // Find the section closest to the center of the viewport
      let closestSection = 0;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((sectionElement, index) => {
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const sectionCenter = window.scrollY + rect.top + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = index;
          }
        }
      });

      setCurrentSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isFormFilled =
    formData.name &&
    formData.type &&
    formData.description &&
    formData.duration &&
    formData.location &&
    (formData.duration !== "custom" ||
      (formData.customDate &&
        formData.customTimeFrom &&
        formData.customTimeTo));

  const handleInputChange = (field) => (e) => {
    let value;

    // Handle direct values (e.g., from select inputs)
    if (typeof e === "string" || typeof e === "number") {
      value = e;
    }
    // Handle event objects (e.g., from input fields)
    else if (e && e.target) {
      value = e.target.value;
    }
    // Handle cases where the input is cleared
    else {
      value = "";
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleScheduleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      duration: value,
      customDate: value !== "custom" ? "" : prev.customDate,
      customTimeFrom: value !== "custom" ? "" : prev.customTimeFrom,
      customTimeTo: value !== "custom" ? "" : prev.customTimeTo,
    }));
  };

  const handleCustomDateChange = (dateRange) => {
    setFormData((prev) => ({ ...prev, customDate: dateRange }));
  };

  const convertTo24Hour = (time12h) => {
    if (!time12h) return "";

    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    }

    return `${hours}:${minutes}`;
  };

  // Modify the fetchEventData function in useEffect
  useEffect(() => {
    const fetchEventData = async () => {
      if (isEditMode) {
        try {
          showLoader();
          const result = await dispatch(
            fetchEventByAny({ key: "_id", value: id })
          );
          if (!result.error) {
            const eventData = result?.payload?.data[0];

            // Convert API time format (12-hour) to 24-hour format for time picker
            const startTime24 = convertTo24Hour(eventData.schedule.startTime);
            const endTime24 = convertTo24Hour(eventData.schedule.endTime);

            // Filter out null members before setting form data
            const validMembers = eventData.members
              ? eventData.members.filter((member) => member !== null)
              : [];

            setFormData({
              _id: eventData._id,
              name: eventData.name,
              type: eventData.type,
              description: eventData.description,
              duration: "custom", // Since we're editing, set to custom
              customDate: `${eventData.schedule.startDate} - ${eventData.schedule.endDate}`,
              customTimeFrom: startTime24, // Use 24-hour format for the time picker
              customTimeTo: endTime24, // Use 24-hour format for the time picker
              position: eventData.position || "",
              manifestoID: eventData.manifestoID || "",
              location: eventData.location[0] || "",
              candidates: eventData.candidates || [],
              members: validMembers, // Use filtered members array
              fileUrl: eventData.fileUrl || "",
              isInviteMembersModalVisible: false,
              isDeleteModalVisible: false,
              memberToDelete: null,
              isSuccessModalVisible: false,
            });
          }
        } catch (error) {
          console.error("Failed to fetch event data:", error);
        } finally {
          hideLoader();
        }
      }
    };

    fetchEventData();
  }, [dispatch, hideLoader, id, isEditMode, showLoader]);

  // Modify the time change handlers to handle both formats
  const handleCustomTimeFromChange = (time24) => {
    // Convert 24-hour time to 12-hour format for API
    const date = new Date(`2000-01-01T${time24}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    setFormData((prev) => ({
      ...prev,
      customTimeFrom: time24, // Store 24-hour format for time picker
      apiTimeFrom: formattedTime, // Store 12-hour format for API
    }));
  };

  const handleCustomTimeToChange = (time24) => {
    // Convert 24-hour time to 12-hour format for API
    const date = new Date(`2000-01-01T${time24}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    setFormData((prev) => ({
      ...prev,
      customTimeTo: time24, // Store 24-hour format for time picker
      apiTimeTo: formattedTime, // Store 12-hour format for API
    }));
  };

  const handleMembersSelected = (newMembers) => {
    setFormData((prev) => ({
      ...prev,
      members: [
        ...prev.members,
        ...newMembers.filter(
          (member) =>
            !prev.members.some((existing) => existing.id === member.id)
        ),
      ],
    }));
  };

  const handleDeleteMember = (member) => {
    setFormData((prev) => ({
      ...prev,
      isDeleteModalVisible: true,
      memberToDelete: member.id,
    }));
  };

  const handleDeleteConfirm = () => {
    showLoader();

    setTimeout(() => {
      hideLoader();
      setFormData((prev) => ({
        ...prev,
        members: prev.members.filter((m) => m.id !== prev.memberToDelete),
        isDeleteModalVisible: false,
        isSuccessModalVisible: true,
      }));
    }, 2000);
  };

  const handleUpdateMember = (memberId, newRole) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.map((member) =>
        member.id === memberId ? { ...member, role: newRole } : member
      ),
    }));
  };

  const handleFileChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      fileUrl: file,
    }));
  };

  const handleSubmit = async () => {
    if (!isFormFilled) return;

    showLoader();

    try {
      const organizationID = getOrganizationId();

      // Only parse date range if custom duration is selected
      let startDate, endDate, startTime, endTime;

      if (formData.duration === "custom") {
        // Handle custom date/time as before
        const [startDateStr, endDateStr] = formData.customDate.split(" - ");

        // Convert date strings to proper format (YYYY-MM-DD)
        const formatDate = (dateStr) => {
          const date = new Date(dateStr);
          return date.toISOString().split("T")[0];
        };

        startDate = formatDate(startDateStr);
        endDate = formatDate(endDateStr);
        startTime = formData.apiTimeFrom || formData.customTimeFrom;
        endTime = formData.apiTimeTo || formData.customTimeTo;
      } else {
        // For fixed durations (24, 48 hours etc)
        const now = new Date();
        startDate = now.toISOString().split("T")[0];

        const hours = parseInt(formData.duration);
        const endDateTime = new Date(now.getTime() + hours * 60 * 60 * 1000);
        endDate = endDateTime.toISOString().split("T")[0];

        // Set default times for fixed durations
        startTime = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        endTime = endDateTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      // Upload file if it's a File object
      let fileUrl = formData.fileUrl;

      if (formData.fileUrl instanceof File) {
        try {
          const uploadResult = await dispatch(
            uploadFile({ file: formData.fileUrl })
          ).unwrap();
          fileUrl = uploadResult.fileUrl;
        } catch (uploadError) {
          console.error("File upload failed:", uploadError);
          // Continue with form submission even if file upload fails
        }
      }

      // Sanitize the description using DOMPurify
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [], // Remove all HTML tags
      });

      // Add safe checks for arrays before mapping
      let members = [];
      if (Array.isArray(formData.members)) {
        try {
          members = formData.members.map((member) => {
            return member.id;
          });
        } catch (memberError) {
          console.error("Error processing members:", memberError);
        }
      }

      let candidates = undefined;
      if (
        Array.isArray(formData.candidates) &&
        formData.candidates.length > 0
      ) {
        try {
          candidates = formData.candidates.map((img) => {
            return img.id;
          });
        } catch (candidateError) {
          console.error("Error processing candidates:", candidateError);
        }
      }

      const eventData = {
        _id: formData._id,
        name: formData.name.trim(),
        type: formData.type,
        description: sanitizedDescription,
        fileUrl: fileUrl,
        schedule: {
          startDate,
          startTime,
          endDate,
          endTime,
        },
        location: [formData.location],
        position: formData.position || undefined,
        candidates: candidates,
        manifestoID: formData.manifestoID,
        organizationID,
        members: members,
      };

      if (isEditMode) {
        await dispatch(updateEvent(eventData)).unwrap();
      } else {
        await dispatch(createEvent(eventData)).unwrap();
      }

      setIsSuccessVisible(true);
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} event:`,
        error
      );
    } finally {
      hideLoader();
    }
  };

  const handleSuccessProceed = () => {
    showLoader();
    setIsSuccessVisible(false);
    setTimeout(() => {
      hideLoader();
      if (isEditMode) {
        navigate(`/dashboard/events/${id}`);
      } else {
        navigate("/dashboard/events"); // Navigate back to events dashboard
      }
    }, 2000);
  };

  const eventTypes = [
    { value: "Campaign", label: "Political Campaign" },
    { value: "Sensitization", label: "Sensitization" },
    { value: "Regular", label: "Regular Event" },
  ];

  const durationOptions = [
    { value: "24", label: "24 Hours" },
    { value: "48", label: "48 Hours" },
    {
      value: "custom",
      label: "Custom",
      customOptions: [
        { value: "72", label: "72 Hours" },
        { value: "96", label: "96 Hours" },
        { value: "120", label: "120 Hours" },
      ],
    },
  ];

  const positionOptions = [
    { value: "president", label: "President" },
    { value: "governor", label: "Governor" },
    { value: "senator", label: "Senator" },
  ];

  const handleManifestoSelectClick = () => {
    if (manifestos.length === 0) {
      // Only fetch if manifestos are not already loaded
      dispatch(getAllManifestos());
    }
  };

  const manifestoOptions = manifestos.map((manifesto) => ({
    value: manifesto._id, // Use the manifesto ID as the value
    label: manifesto.name, // Use the manifesto name as the label
  }));

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      dispatch(getAllManifestos());
    }
  }, [dispatch]);

  const roleOptions = [
    { value: "speaker", label: "Speaker" },
    { value: "coordinator", label: "Coordinator" },
    { value: "participant", label: "Participant" },
  ];

  // breadcrumb items
  const items = [
    { title: "Events", link: "dashboard/events" },
    { title: "Add New", link: "" },
  ];

  return (
    <div className="w-full grid-cols-4 gap-10 py-6 text-left lg:grid sm:py-0">
      <div className="col-span-3 lg:border-r">
        <Breadcrumb items={items} />
        <div className="flex items-center justify-between mb-6">
          <Back />
        </div>
        <div className="my-5 rounded-lg lg:p-5 lg:border">
          <div className="w-full">
            <h1 className="mb-2 text-left heading-medium sm:text-3xl">
              {isEditMode
                ? `Welcome ${userFirstName}, Edit your Event`
                : `Welcome ${userFirstName}, Setup your Event`}
            </h1>
            <p className="mb-6 text-left text-small">
              Let&apos;s get started with creating your event. Fill in the basic
              information below.
            </p>

            {/* Section 1: Event Information */}
            <div
              ref={(element) => {
                sectionRefs.current[0] = element;
              }}>
              <h1 className="mb-4 font-semibold text-left text-bold sm:text-2xl">
                Describe what this event is all about
              </h1>
              <div className="space-y-6">
                <Input
                  label="Title"
                  placeholder="Enter event name"
                  value={formData.name}
                  onChange={handleInputChange("name")}
                />
                <Input
                  type="select"
                  label="Type"
                  placeholder="Select event type"
                  value={formData.type}
                  onChange={handleInputChange("type")}
                  options={eventTypes}
                />
                <TextEditor
                  label="Description"
                  placeholder="Enter Texts"
                  value={formData.description}
                  onChange={handleEditorChange}
                />
              </div>
            </div>

            {/* Section 2: Event Image */}
            <div
              ref={(element) => {
                sectionRefs.current[1] = element;
              }}
              className="mt-8">
              <div className="space-y-2">
                <h2 className="text-left text-bold">Event Image</h2>
                <p className="text-left text-gray-500 text-small">
                  Add an image to make your event more visually appealing
                </p>
                <FileInput
                  id="event-image-upload"
                  label="UPLOAD IMAGE"
                  placeholder={
                    formData.fileUrl
                      ? formData.fileUrl instanceof File
                        ? formData.fileUrl.name
                        : "File uploaded"
                      : "event-image.png"
                  }
                  onChange={handleFileChange}
                />
              </div>
            </div>

            {/* Section 3: Members & Manifesto (conditional) */}
            {formData.type &&
              ["Campaign", "Sensitization"].includes(formData.type) && (
                <div
                  ref={(element) => {
                    sectionRefs.current[2] = element;
                  }}
                  className="mt-8">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <h2 className="text-left text-bold">
                        {formData.type === "Campaign" ? "Candidate" : "Invites"}
                      </h2>
                      <p className="text-left text-gray-500 text-small">
                        {formData.type === "Campaign"
                          ? "Who is this campaign event about"
                          : "Members allocated to host the event"}
                      </p>
                      {formData.type === "Campaign" && (
                        <Input
                          type="select"
                          label="POSITION CAMPAIGN FOR"
                          placeholder="Select position"
                          value={formData.position}
                          onChange={handleInputChange("position")}
                          options={positionOptions}
                        />
                      )}
                      <MemberPicker
                        members={formData.members}
                        roles={roleOptions}
                        onOpenModal={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isInviteMembersModalVisible: true,
                          }))
                        }
                        onDeleteMember={handleDeleteMember}
                        onUpdateMember={handleUpdateMember}
                      />

                      <InviteMembersDialog
                        isVisible={formData.isInviteMembersModalVisible}
                        onClose={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isInviteMembersModalVisible: false,
                          }))
                        }
                        onMembersSelected={handleMembersSelected}
                        modalTitle="Add New Candidates"
                        primaryHeader="Presidential Candidates"
                        showSecondaryHeader={true}
                        secondaryHeader="Gubernatorial Candidates"
                        userRoleDisplay="select"
                        roleOptions={roleOptions}
                        invitedMembers={formData.members}
                        emptyInvitedMessage="No candidates have been invited yet."
                        onUpdateMember={handleUpdateMember}
                      />

                      <Delete
                        isVisible={formData.isDeleteModalVisible}
                        onClose={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isDeleteModalVisible: false,
                          }))
                        }
                        onProceed={handleDeleteConfirm}
                        header={`Remove ${
                          formData.members.find(
                            (m) => m.id === formData.memberToDelete
                          )?.name
                        }`}
                        paragraph={`Are you sure you want to remove <span class="text-primary">${
                          formData.members.find(
                            (m) => m.id === formData.memberToDelete
                          )?.name
                        }</span> from this event?`}
                        proceedButtonText="Remove"
                        cancelButtonText="Cancel"
                      />

                      <Success
                        isVisible={formData.isSuccessModalVisible}
                        onClose={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isSuccessModalVisible: false,
                          }))
                        }
                        header="Member Removed"
                        paragraph={`${
                          formData.members.find(
                            (m) => m.id === formData.memberToDelete
                          )?.name
                        } has been removed from this event.`}
                        buttonText="Close"
                        onButtonClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isSuccessModalVisible: false,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <h2 className="text-left text-bold">Manifesto</h2>
                      <p className="text-left text-gray-500 text-small">
                        Select a Manifesto to go along with this{" "}
                        {formData.type === "Campaign"
                          ? "Campaign"
                          : "Sensitization"}
                      </p>
                      <Input
                        type="select"
                        label="MANIFESTO"
                        placeholder="Select manifesto"
                        value={formData.manifestoID}
                        onChange={handleInputChange("manifestoID")}
                        onClick={handleManifestoSelectClick}
                        options={manifestoOptions}
                      />
                      {manifestoOptions.length === 0 && (
                        <p className="text-left text-small text-darkGrey">
                          No manifestos yet. Create one in the Manifesto module,
                          then come back to attach it.{" "}
                          <Link
                            to="/dashboard/manifesto/add"
                            className="text-primary underline font-medium">
                            Go to Manifesto
                          </Link>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Section 4: Schedule */}
            <div
              ref={(element) => {
                sectionRefs.current[3] = element;
              }}
              className="mt-8">
              <div className="space-y-2">
                <h1 className="text-left text-bold">Schedule</h1>
                <p className="text-left text-gray-500 text-small">
                  Select a Manifesto to go along with this campaign
                </p>
                <CustomScheduleInput
                  options={durationOptions}
                  value={formData.duration}
                  onChange={handleScheduleChange}
                  onCustomDateChange={handleCustomDateChange}
                  onCustomTimeFromChange={handleCustomTimeFromChange}
                  onCustomTimeToChange={handleCustomTimeToChange}
                  customDate={formData.customDate}
                  customTimeFrom={formData.customTimeFrom}
                  customTimeTo={formData.customTimeTo}
                />
              </div>
            </div>

            {/* Section 5: Location */}
            <div
              ref={(element) => {
                sectionRefs.current[4] = element;
              }}
              className="mt-8">
              <div className="space-y-2">
                <h2 className="text-left text-bold">Location</h2>
                <p className="text-left text-gray-500 text-small">
                  Add locations where you&apos;d like this campaign to be seen
                </p>
                <LocationInput
                  label="LOCATION"
                  placeholder="Enter event location"
                  value={formData.location}
                  onChange={handleInputChange("location")}
                  id="event-location"
                />
              </div>

              <Button
                text="Proceed"
                icon={<ArrowRightOutlined />}
                onClick={handleSubmit}
                isActive={!!isFormFilled}
                color="darkBlue"
                className={`w-full mt-6 ${
                  isFormFilled
                    ? "bg-primary text-white"
                    : "bg-darkBlue text-primary"
                }`}
              />
            </div>
          </div>
          {/* Success Dialog */}
          <Success
            isVisible={isSuccessVisible}
            header={
              isEditMode ? "Event Updated Successfully" : "Event Details Saved"
            }
            paragraph={
              isEditMode
                ? "Your event has been successfully updated."
                : "Your event details have been saved. You can proceed to the next step."
            }
            buttonText="Proceed"
            showCancelButton={false}
            onButtonClick={handleSuccessProceed}
          />
        </div>
      </div>
      {/* Steps component for single-page form */}
      <div className="hidden mt-10 lg:block h-[90vh] sticky top-[80px]">
        <StepsComponent
          current={currentSection}
          steps={formSections}
          mode="single-page"
          sectionsCompleted={sectionsCompleted}
        />
      </div>
    </div>
  );
};

export default AddEvent;
